import { Link, useFetcher } from "@remix-run/react";
import { useId } from "react";

import AttributionFields from "~/components/forms/AttributionFields";
import { CONTACT_EMAIL, CONTACT_PHONE } from "~/config/constants";
import { useBooking } from "~/context/BookingContext";
import { COURSES } from "~/data/courses";
import { INTENSITIES } from "~/data/pricing";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import { useDialog } from "~/hooks/useDialog";
import common from "~/i18n/common";
import coursesDict from "~/i18n/courses";
import pricingDict from "~/i18n/pricing";
import { useT } from "~/i18n/useT";

import "./BookingModal.scss";

type ActionData = { success?: boolean; error?: string };

export default function BookingModal() {
  const { isOpen, close, presetCourse } = useBooking();
  const t = useT(common);
  const c = useT(coursesDict);
  const p = useT(pricingDict);
  const localize = useLocalizedPath();
  const fetcher = useFetcher<ActionData>();
  const ref = useDialog(isOpen, close);

  const titleId = useId();
  const nameId = useId();
  const emailId = useId();
  const courseId = useId();
  const trackId = useId();
  const timeId = useId();
  const consentId = useId();

  const busy = fetcher.state !== "idle";
  const sent = fetcher.data?.success === true;

  if (!isOpen) return null;

  return (
    // Clicking the backdrop closes; comparing target to currentTarget avoids
    // putting a click handler on the dialog itself just to stop propagation.
    // Keyboard users close with Escape, handled by useDialog.
    <div
      className="ss-modal"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="ss-modal__panel"
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <aside className="ss-modal__aside">
          <div className="ss-glow ss-glow--blue ss-modal__glow" />
          <div className="ss-modal__aside-body">
            <p className="ss-eyebrow">{t.booking.eyebrow}</p>
            <h2 className="ss-modal__title" id={titleId}>
              {t.booking.title}
            </h2>
            <p className="ss-modal__lead">{t.booking.body}</p>

            <ul className="ss-modal__benefits">
              {t.booking.benefits.map((benefit) => (
                <li key={benefit}>
                  <span aria-hidden="true">✓</span>
                  {benefit}
                </li>
              ))}
            </ul>

            <p className="ss-modal__contact">
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              <br />
              <a href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}>
                {CONTACT_PHONE}
              </a>
            </p>
          </div>
        </aside>

        <div className="ss-modal__main">
          <button
            type="button"
            className="ss-modal__close"
            aria-label={t.booking.close}
            onClick={close}
          >
            <span aria-hidden="true">×</span>
          </button>

          {sent ? (
            <div className="ss-modal__success" role="status">
              <span className="ss-modal__check" aria-hidden="true">
                ✓
              </span>
              <h3 className="ss-modal__success-title">
                {t.booking.successTitle}
              </h3>
              <p className="ss-modal__success-body">{t.booking.successBody}</p>
              <button
                type="button"
                className="ss-btn ss-btn--primary"
                onClick={close}
              >
                {t.cta.done}
              </button>
            </div>
          ) : (
            <fetcher.Form method="post" action="/booking/request">
              <h3 className="ss-modal__form-title">{t.booking.formTitle}</h3>

              <div className="ss-modal__fields">
                <div className="ss-modal__row">
                  <div className="ss-field">
                    <label className="ss-field__label" htmlFor={nameId}>
                      {t.booking.fullName}
                    </label>
                    <input
                      id={nameId}
                      className="ss-field__input"
                      name="fullName"
                      required
                      autoComplete="name"
                      placeholder={t.booking.fullNamePlaceholder}
                    />
                  </div>
                  <div className="ss-field">
                    <label className="ss-field__label" htmlFor={emailId}>
                      {t.booking.email}
                    </label>
                    <input
                      id={emailId}
                      className="ss-field__input"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder={t.booking.emailPlaceholder}
                    />
                  </div>
                </div>

                <div className="ss-field">
                  <label className="ss-field__label" htmlFor={courseId}>
                    {t.booking.courseOfInterest}
                  </label>
                  <select
                    id={courseId}
                    className="ss-field__select"
                    name="course"
                    defaultValue={presetCourse ?? ""}
                  >
                    {COURSES.map((course) => (
                      <option key={course.slug} value={course.slug}>
                        {c[course.slug].title}
                      </option>
                    ))}
                    <option value="">{t.booking.notSureYet}</option>
                  </select>
                </div>

                <div className="ss-modal__row">
                  <div className="ss-field">
                    <label className="ss-field__label" htmlFor={trackId}>
                      {t.booking.preferredTrack}
                    </label>
                    <select
                      id={trackId}
                      className="ss-field__select"
                      name="track"
                    >
                      {INTENSITIES.map((key) => (
                        <option key={key} value={key}>
                          {p.tierNames[key]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ss-field">
                    <label className="ss-field__label" htmlFor={timeId}>
                      {t.booking.bestTime}
                    </label>
                    <select
                      id={timeId}
                      className="ss-field__select"
                      name="time"
                    >
                      <option value="morning">{t.booking.times.morning}</option>
                      <option value="afternoon">
                        {t.booking.times.afternoon}
                      </option>
                      <option value="evening">{t.booking.times.evening}</option>
                    </select>
                  </div>
                </div>

                <AttributionFields />

                <div className="ss-honeypot" aria-hidden="true">
                  <label htmlFor={`${nameId}-address`}>Address</label>
                  <input
                    id={`${nameId}-address`}
                    name="address"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Art. 7 GDPR: unticked by default, and the policy is one click away. */}
                <label className="ss-consent" htmlFor={consentId}>
                  <input
                    id={consentId}
                    type="checkbox"
                    name="consent"
                    value="yes"
                    required
                  />
                  <span>
                    {t.form.consentLabel}{" "}
                    <Link
                      to={localize("/privacy")}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t.form.consentLink}
                    </Link>{" "}
                    {t.form.consentSuffix}
                  </span>
                </label>

                {fetcher.data?.error ? (
                  <p
                    className="ss-form-status ss-form-status--error"
                    role="alert"
                  >
                    {fetcher.data.error === "RATE_LIMITED"
                      ? t.form.rateLimited
                      : t.booking.error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="ss-btn ss-btn--primary ss-btn--block ss-modal__submit"
                  disabled={busy}
                  aria-disabled={busy}
                >
                  {busy ? t.booking.submitting : t.booking.submit}
                </button>

                <p className="ss-modal__disclaimer">{t.booking.disclaimer}</p>
              </div>
            </fetcher.Form>
          )}
        </div>
      </div>
    </div>
  );
}
